/**
 * 시도 목록과 지도 도형(`koreaMapPaths.ts`) 사이의 다리.
 *
 * 서버(`fevent.region-stat.ts`)가 주소를 `서울`/`경기`/`전북`… 17개 키로 정규화해 주고,
 * 지도 도형은 통계청 코드(2자리)를 쓴다. 여기서 둘을 잇는다.
 * 지도 원자료가 2018년 경계라 이후 바뀐 이름(전북·강원 특별자치도)은 키 쪽이 이미 흡수한다.
 *
 * ⚠️ 이 파일은 지도 도형을 import하지 않는다 — 도형 모듈(270KB)은 지도 컴포넌트에서만
 * 지연 로드해 관리자 통계를 열지 않는 사용자의 번들에 실리지 않게 한다.
 */

export interface KoreaSido {
  /** 서버 정규화 키 */
  key: string;
  /** 통계청 시도 코드 (지도 도형 code, 시군구 code 앞 2자리) */
  code: string;
  name: string;
}

export const KOREA_SIDOS: readonly KoreaSido[] = [
  { key: '서울', code: '11', name: '서울특별시' },
  { key: '부산', code: '21', name: '부산광역시' },
  { key: '대구', code: '22', name: '대구광역시' },
  { key: '인천', code: '23', name: '인천광역시' },
  { key: '광주', code: '24', name: '광주광역시' },
  { key: '대전', code: '25', name: '대전광역시' },
  { key: '울산', code: '26', name: '울산광역시' },
  { key: '세종', code: '29', name: '세종특별자치시' },
  { key: '경기', code: '31', name: '경기도' },
  { key: '강원', code: '32', name: '강원특별자치도' },
  { key: '충북', code: '33', name: '충청북도' },
  { key: '충남', code: '34', name: '충청남도' },
  { key: '전북', code: '35', name: '전북특별자치도' },
  { key: '전남', code: '36', name: '전라남도' },
  { key: '경북', code: '37', name: '경상북도' },
  { key: '경남', code: '38', name: '경상남도' },
  { key: '제주', code: '39', name: '제주특별자치도' },
];

/** 주소를 시도로 못 알아본 상가의 묶음 키 (서버가 '' 로 준다) */
export const UNRESOLVED_SIDO_KEY = '';
export const UNRESOLVED_SIDO_LABEL = '미분류';

const SIDO_BY_KEY = new Map(KOREA_SIDOS.map((sido) => [sido.key, sido]));
const SIDO_BY_CODE = new Map(KOREA_SIDOS.map((sido) => [sido.code, sido]));

export const sidoOfKey = (key: string): KoreaSido | undefined => SIDO_BY_KEY.get(key);
export const sidoOfCode = (code: string): KoreaSido | undefined =>
  SIDO_BY_CODE.get(code.slice(0, 2));

/** 화면 표기 — 키가 곧 짧은 이름이고, 미분류만 라벨을 바꾼다 */
export const sidoLabelOf = (key: string): string =>
  key === UNRESOLVED_SIDO_KEY ? UNRESOLVED_SIDO_LABEL : key;

/** 시군구 표기를 지도 도형 이름과 맞춘다 — 공백만 정리(둘째 단위까지라 보통 그대로) */
export const sigunguShapeNameOf = (sigungu: string): string =>
  sigungu.replace(/\s+/g, '');

/** 지도 라벨은 마지막 단위만 (표기에 공백이 섞여 와도 안전하게) */
export const sigunguShortLabelOf = (sigungu: string): string => {
  const parts = sigungu.trim().split(/\s+/);
  return parts[parts.length - 1] ?? sigungu;
};
